import React, { useCallback, useState } from 'react';
import { Calendar, DollarSign } from 'lucide-react';
import { Modal } from '@components/molecules/Modal';
import { FormGroup } from '@components/molecules/form/FormGroup';
import { Input } from '@components/atoms/Input';
import { TextareaInput } from '@components/atoms/TextareaInput';
import ReactSelect, { SelectOption } from '@components/atoms/ReactSelect';
import { Button } from '@components/atoms/Button';
import { BudgetRequestCreateInput } from '@models/budget.model';
import { formatForDisplay } from '@utils/formatDate';
import { FileDropZone } from '@components/molecules/FileDropZone';
import { Organization } from '@models/organization.model';
import { Task } from '@models/task.model';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { graphQlClient } from '@config/graphqlClient';
import OrganizationsGraphQL from '@graphql/organization.queries';
import { parseTaskDate, formatTaskDateForBackend } from '@utils/dateOnlyParser';
import { htmlToPlainText } from '@utils/richText';

interface BudgetRequestModalProps {
  open: boolean;
  onClose: () => void;
  // Returns a promise so the modal can hold its uploading state for the real
  // duration of the request. Typed as `void` it looked synchronous, and the
  // upload state was set and cleared in the same tick.
  onSubmit: (input: BudgetRequestCreateInput) => void | Promise<void>;
  task: Task;
  isSubmitting: boolean;
}

const validationSchema = Yup.object({
  requestedStartDate: Yup.date()
    .required('Start date is required')
    .typeError('Invalid date'),
  requestedEndDate: Yup.date()
    .required('End date is required')
    .min(Yup.ref('requestedStartDate'), 'End date must be after start date')
    .typeError('Invalid date'),
  maxBudgetAmount: Yup.number()
    .positive('Amount must be positive')
    .typeError('Invalid amount'),
  description: Yup.string(),
  recipientOrganizationIds: Yup.array()
    .of(Yup.string())
    .min(1, 'At least one recipient organization is required'),
});

const BudgetRequestModal: React.FC<BudgetRequestModalProps> = ({
  open,
  onClose,
  onSubmit,
  task,
  isSubmitting,
}) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loadingOrganizations, setLoadingOrganizations] = useState(false);

  const fetchOrganizations = useCallback(async () => {
    setLoadingOrganizations(true);
    try {
      const data: any = await graphQlClient.request(
        OrganizationsGraphQL.queries.getOrganizationsForTaskCreation,
        {
          organizations: {
            page: 1,
            limit: 100,
          },
        },
      );

      // Filter for subcontractors and exclude originalOrganization
      const subcontractors = (
        data.getOrganizationsForTaskCreation?.organizations || []
      ).filter((org: Organization) =>
        org.showAsSubcontractor &&
        org.organizationId !== task.originalOrganizationId
      );
      setOrganizations(subcontractors);
    } catch (error) {
      console.error('Error fetching organizations:', error);
    } finally {
      setLoadingOrganizations(false);
    }
  }, [task.originalOrganizationId]);

  // Fetch organizations when modal opens
  React.useEffect(() => {
    if (open) {
      fetchOrganizations();
    }
  }, [open, fetchOrganizations]);

  const formik = useFormik({
    initialValues: {
      requestedStartDate: task.startTime
        ? formatTaskDateForBackend(parseTaskDate(task.startTime)) || ''
        : '',
      requestedEndDate: task.endTime
        ? formatTaskDateForBackend(parseTaskDate(task.endTime)) || ''
        : '',
      maxBudgetAmount: task.budget ? task.budget.toString() : '',
      description: htmlToPlainText(task.description) || '',
      recipientOrganizationIds: [] as string[],
    },
    validationSchema,
    onSubmit: async (values) => {
      const input: BudgetRequestCreateInput = {
        taskId: task.taskId!,
        requestedStartDate: values.requestedStartDate,
        requestedEndDate: values.requestedEndDate,
        maxBudgetAmount: values.maxBudgetAmount
          ? parseFloat(values.maxBudgetAmount)
          : undefined,
        description: values.description || undefined,
        recipientOrganizationIds: values.recipientOrganizationIds,
        files: selectedFiles.length > 0 ? selectedFiles : undefined,
      };

      if (selectedFiles.length > 0) {
        setIsUploading(true);
      }

      try {
        await onSubmit(input);
      } finally {
        setIsUploading(false);
      }
    },
  });

  const handleClose = () => {
    if (isUploading) return; // Prevent closing during upload
    formik.resetForm();
    setSelectedFiles([]);
    onClose();
  };

  const organizationOptions: SelectOption[] = organizations.map((org) => ({
    value: org.organizationId,
    label: org.name || '',
  }));

  const selectedOrganizations = organizationOptions.filter((option) =>
    formik.values.recipientOrganizationIds.includes(String(option.value)),
  );

  const actionButtons = (
    <>
      <Button
        size="md"
        variant="secondary"
        type="button"
        onClick={handleClose}
        disabled={isSubmitting || isUploading}>
        Cancel
      </Button>
      <Button
        size="md"
        variant="primary"
        type="button"
        onClick={() => formik.handleSubmit()}
        disabled={
          isSubmitting || isUploading || !formik.isValid || !formik.dirty
        }>
        {isSubmitting
          ? 'Creating...'
          : isUploading
            ? 'Uploading...'
            : 'Create Bid Request'}
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={open}
      onClose={handleClose}
      title="Create Bid Request for Task"
      size="large"
      actionButtons={actionButtons}>
      <form onSubmit={formik.handleSubmit} className="flex flex-col gap-5">
        {/* Task summary — read-only context for the bid being requested */}
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-900 dark:bg-amber-950/30">
          <h3 className="mb-2 text-sm font-semibold text-amber-800 dark:text-amber-300">
            Task Details
          </h3>
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {task.name}
          </p>
          {task.description && (
            <p className="mt-1 line-clamp-3 whitespace-pre-line text-sm text-gray-500 dark:text-gray-400">
              {htmlToPlainText(task.description)}
            </p>
          )}
          <div className="mt-2 flex flex-wrap gap-4">
            {task.startTime && task.endTime && (
              <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                <Calendar size={14} className="text-amber-500" />
                {formatForDisplay(task.startTime)} -{' '}
                {formatForDisplay(task.endTime)}
              </span>
            )}
            {task.budget && (
              <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                <DollarSign size={14} className="text-amber-500" />
                Budget: ${task.budget.toLocaleString()}
              </span>
            )}
            {task.manHours && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Est. Hours: {task.manHours}
              </span>
            )}
          </div>
        </div>

        <div className="flex gap-4">
          <FormGroup
            label="Start Date"
            htmlFor="requestedStartDate"
            error={
              formik.touched.requestedStartDate
                ? formik.errors.requestedStartDate
                : undefined
            }>
            <Input
              id="requestedStartDate"
              name="requestedStartDate"
              type="date"
              iconLeft={<Calendar size={16} className="text-amber-500" />}
              value={formik.values.requestedStartDate}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.requestedStartDate &&
                Boolean(formik.errors.requestedStartDate)
              }
            />
          </FormGroup>
          <FormGroup
            label="End Date"
            htmlFor="requestedEndDate"
            error={
              formik.touched.requestedEndDate
                ? formik.errors.requestedEndDate
                : undefined
            }>
            <Input
              id="requestedEndDate"
              name="requestedEndDate"
              type="date"
              iconLeft={<Calendar size={16} className="text-amber-500" />}
              value={formik.values.requestedEndDate}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.requestedEndDate &&
                Boolean(formik.errors.requestedEndDate)
              }
            />
          </FormGroup>
        </div>

        <FormGroup
          label="Maximum Bid Amount (Optional)"
          htmlFor="maxBudgetAmount"
          error={
            formik.touched.maxBudgetAmount
              ? formik.errors.maxBudgetAmount
              : undefined
          }>
          <Input
            id="maxBudgetAmount"
            name="maxBudgetAmount"
            type="number"
            iconLeft={<DollarSign size={16} className="text-amber-500" />}
            value={formik.values.maxBudgetAmount}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.maxBudgetAmount &&
              Boolean(formik.errors.maxBudgetAmount)
            }
          />
        </FormGroup>

        <FormGroup
          label="Select Recipient Organizations"
          htmlFor="recipientOrganizationIds"
          error={
            formik.touched.recipientOrganizationIds
              ? (formik.errors.recipientOrganizationIds as string | undefined)
              : undefined
          }>
          <ReactSelect
            inputId="recipientOrganizationIds"
            isMulti
            options={organizationOptions}
            value={selectedOrganizations}
            isLoading={loadingOrganizations}
            placeholder="Choose one or more organizations"
            error={
              formik.touched.recipientOrganizationIds &&
              Boolean(formik.errors.recipientOrganizationIds)
            }
            onChange={(value) => {
              const selected = (value as SelectOption[] | null) ?? [];
              formik.setFieldValue(
                'recipientOrganizationIds',
                selected.map((option) => String(option.value)),
              );
            }}
            onBlur={() =>
              formik.setFieldTouched('recipientOrganizationIds', true)
            }
          />
        </FormGroup>

        <FormGroup
          label="Additional Description (Optional)"
          htmlFor="description"
          error={
            formik.touched.description ? formik.errors.description : undefined
          }>
          <TextareaInput
            id="description"
            name="description"
            rows={4}
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.description && Boolean(formik.errors.description)
            }
          />
        </FormGroup>

        <FormGroup label="Attach Documents (Optional)" htmlFor="documents">
          <FileDropZone
            files={selectedFiles}
            onFilesChange={setSelectedFiles}
            disabled={isUploading}
            isUploading={isUploading}
            activeColor="indigo"
          />
        </FormGroup>
      </form>
    </Modal>
  );
};

export default BudgetRequestModal;
